import chunk from 'lodash/chunk';
import flatten from 'lodash/flatten';
import max from 'lodash/max';
import min from 'lodash/min';
import { combineLatest, concat, Observable, of } from 'rxjs';
import { bufferCount, map, switchMap, take } from 'rxjs/operators';
import { formatDayFromUtc } from '../../../utils/date';
import { testMetaString } from '../../../utils/testMetaString';
import {
  Entity,
  EntityType,
  IAthlete,
  IComputedAggregate,
  ICrossTestMetricSelection,
  IRange,
  ISelectableEntity,
  ITag,
  ITestType,
  IUser,
} from '../../types';
import {
  IRangeQueryArgs,
  mergeMetrics,
  rangeQuery,
} from '../aggregatesProvider';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { entitiesProvider, IEntities } from '../entitiesProvider';
import {
  blockReportSettingsProvider,
  IBlockReportSettings,
  IBlockReportSettingsState,
} from '../settings/reports/blockReportSettingsProvider';
import { ISettingsTemplate } from '../settings/reports/types';
import { authenticatedUserProvider } from '../userProvider';
import { percentChange } from '../utils/calculateStatistics';
import { gatherQueryHashes } from '../utils/gatherQueryHashes';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IReportState } from './types';

export interface IBlockReportState extends IReportState {
  hashes: string[];
  settings: ISettingsTemplate<IBlockReportSettings>;
  blockResult: IBlockResult[];
  blockTimeframe: IRange;
  isMaxDatapoints: boolean;
}

interface IEntityQueryArg {
  id: string;
  type: EntityType;
  entity: Entity;
}

interface IEntitiesStageData extends IBlockReportSettingsState {
  entityQueries: IEntityQueryArg[];
}

export interface ITargetData {
  entity: Entity;
  data: ICrossTestAggregates;
}

interface ITargetStageData extends IBlockReportSettingsState {
  targetData: ITargetData[];
  hashes: string[];
}

/**
 * @description pipeline that observes the aggregates requested based on the block report settings
 * and then determines and emits the state of the report
 * @param entityType
 * @param entityId
 * @returns
 */
export const blockReportProvider = (
  entityType: EntityType,
  entity: Entity
): Observable<IBlockReportState> => {
  return blockReportSettingsProvider().pipe(
    // this stage gets the individual athletes if selected entity is a group or a team
    switchMap(
      (settings: IBlockReportSettingsState): Observable<IEntitiesStageData> => {
        // Returns array of athletes if entity is group or team, otherwise returns just the single athlete
        if (entityType === 'groups' || entityType === 'teams') {
          return entitiesProvider().pipe(
            map((entities: IEntities): IEntitiesStageData => {
              /// filter the athletes that are members of the group or team
              const athletes = entities.athletes.filter(
                (a: IAthlete): boolean => a[entityType].includes(entity.id)
              );

              return {
                // IBlockReportSettingsState
                ...settings,
                // IEntityQueryArg[]
                entityQueries: athletes.map(
                  (mEntity: IAthlete): IEntityQueryArg => ({
                    id: mEntity.id,
                    type: 'athletes',
                    entity: mEntity,
                  })
                ),
              };
            })
          );
        } else {
          ///  the selected entity is an athlete
          return of({
            ...settings,
            entityQueries: [{ id: entity.id, type: entityType, entity }],
          });
        }
      }
    ),
    /// this stage fetches the target data for the entities
    switchMap(
      (
        settingsAndEntities: IEntitiesStageData
      ): Observable<ITargetStageData> => {
        const settingsValidation = validateSettings(settingsAndEntities);
        if (settingsValidation.isValid) {
          if (settingsAndEntities.entityQueries.length > 0) {
            const hashes = gatherQueryHashes(
              settingsAndEntities.selected.settings.metrics
            );

            /// create an array of observables that will make 100 queries each
            const chunkedQueries = chunk(
              settingsAndEntities.entityQueries,
              100
            ).map((mChunk: IEntityQueryArg[]): Observable<ITargetData[]> => {
              /// combine the 100 queries into a single emission
              return combineLatest(
                mChunk.map((qe: IEntityQueryArg): Observable<ITargetData> => {
                  return crossTestAggregates(
                    hashes ?? [],
                    qe.id,
                    qe.type as EntityType,
                    settingsAndEntities.timeframe.dateType,
                    settingsAndEntities.timeframe.dateRange
                  ).pipe(
                    /// complete the observable to clean up the upstream listener
                    take(1),
                    map((data: ICrossTestAggregates): ITargetData => {
                      return {
                        entity: qe.entity,
                        data,
                      };
                    })
                  );
                })
              );
            });
            /// process each emission sequentially
            return concat(...chunkedQueries).pipe(
              /// collect the results until they are all complete
              bufferCount(chunkedQueries.length),
              /// flatten the results
              map((targetData: ITargetData[][]): ITargetStageData => {
                return {
                  targetData: flatten(targetData),
                  ...settingsAndEntities,
                  hashes,
                };
              })
            );
          } else {
            const targetStageData: ITargetStageData = {
              ...settingsAndEntities,
              hashes: null,
              targetData: [],
            };
            return of(targetStageData);
          }
        } else {
          return of({
            ...settingsAndEntities,
            targetData: [],
            hashes: null,
          });
        }
      }
    ),
    /// map the aggregates to a state
    map((results: ITargetStageData): IBlockReportState => {
      const blockResult = makeBlockResults(
        results.isMaxDatapoints,
        results.selected?.settings,
        results.targetData
      );

      /// validate settings
      const settingsValidation = validateSettings(results);
      if (!settingsValidation.isValid) {
        return {
          hashes: results.hashes,
          ...settingsValidation,
          blockTimeframe: results?.timeframe?.dateRange,
          settings: results?.selected,
          blockResult,
          isMaxDatapoints: results?.isMaxDatapoints,
        };
      }

      /// validate data
      const dataValidation = validateData(blockResult);

      return {
        hashes: results.hashes,
        settings: results.selected,
        blockTimeframe: results.timeframe.dateRange,
        blockResult,
        isMaxDatapoints: results.isMaxDatapoints,
        ...dataValidation,
      };
    })
  );
};

export interface IBlockResult {
  hashKeyVals: unknown;
  entity: Entity;
  data: IBlockData[];
}

export interface IBlockData {
  hash: string;
  testType: string;
  metric: {
    direction: 1 | -1 | 0;
    label: string;
    value: string;
    changeCutoffs: { lowerCutoff: number; upperCutoff: number };
    units: string;
  };
  tags: ISelectableEntity[];
  baseline: null | number;
  comparison: null | number;
  percentage: null | number;
  blockTimeframe: IRange;
  start: string;
  end: string;
  roundingPrecision: number;
}

/**
 * @description generates the individual block result for each metric defined in the report settings
 * @param settings
 * @param aggregates
 * @returns
 */
const makeBlockResults = (
  isMaxDatapoints: boolean,
  settings: IBlockReportSettings,
  results: ITargetData[]
): IBlockResult[] => {
  return results.map((athleteResult: ITargetData): IBlockResult => {
    const hashKeyVals = {};
    const entity = athleteResult.entity;
    const data = settings?.metrics?.map(
      (metricAndHash: ICrossTestMetricSelection): IBlockData => {
        const hash = metricAndHash?.queryHash;
        const metricValue = metricAndHash?.metric?.value;
        const metricLabel = metricAndHash?.metric?.label;
        const metricDirection = metricAndHash?.metric?.entity?.direction;
        const metricUnits = metricAndHash?.metric?.entity?.units;
        const roundingPrecision =
          metricAndHash?.metric?.entity?.roundingPrecision;
        const baselineSession = athleteResult?.data[hash]?.sessions[0];
        const compSession = athleteResult?.data[hash]?.sessions[1];

        const setMaxOrMean = (
          direction: number,
          session: IComputedAggregate
        ): { timestamp: string; value: number } => {
          if (isMaxDatapoints) {
            if (direction === 1) {
              return {
                timestamp: session.meta.id,
                value: max(session.data[metricValue]?.topValues ?? []),
              };
            } else if (direction === -1) {
              return {
                timestamp: session.meta.id,
                value: min(session.data[metricValue]?.bottomValues ?? []),
              };
            }
          } else {
            return {
              timestamp: session.meta.id,
              value: session.data[metricValue]?.mean,
            };
          }
        };

        const baselineVals = baselineSession
          ? setMaxOrMean(metricDirection, baselineSession)
          : null;

        const compVals = compSession
          ? setMaxOrMean(metricDirection, compSession)
          : null;

        const percentage =
          baselineVals === null ||
          compVals === null ||
          !baselineVals?.value ||
          !compVals?.value
            ? null
            : percentChange(metricUnits, compVals.value, baselineVals.value);

        const hashAndMetric = `${hash + metricValue}`;

        // This step creates an object with unique hash+metric keys which is used to sort the data table
        hashKeyVals[hashAndMetric] = percentage;

        const testMeta = testMetaString(
          (metricAndHash?.testType?.entity as ITestType).abbr,
          metricAndHash?.tags?.map(
            (v: ISelectableEntity): ITag => v.entity as ITag
          )
        );

        return {
          hash,
          testType: testMeta,
          metric: {
            direction: metricDirection,
            label: metricLabel,
            value: metricValue,
            units: metricUnits,
            changeCutoffs: {
              lowerCutoff: metricAndHash.changeCutoffs?.lowerCutoff,
              upperCutoff: metricAndHash.changeCutoffs?.upperCutoff,
            },
          },
          roundingPrecision,
          tags: metricAndHash?.tags,
          baseline: baselineVals === null ? null : baselineVals.value,
          comparison: compVals === null ? null : compVals.value,
          percentage,
          blockTimeframe: settings.timeframe,
          start:
            baselineVals === null
              ? null
              : formatDayFromUtc(+baselineVals.timestamp),
          end: compVals === null ? null : formatDayFromUtc(+compVals.timestamp),
          [`${hash + metricValue}`]: percentage,
        };
      }
    );

    return {
      hashKeyVals,
      entity,
      data,
    };
  });
};

interface IValidationResult {
  isValid: boolean;
  errorCode?: string;
}

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateSettings = (
  settings: IBlockReportSettingsState
): IValidationResult => {
  if (!settings?.selected) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  }
  /// make sure a least one metric is defined
  else if (
    !settings?.selected?.settings?.metrics ||
    settings?.selected?.settings?.metrics?.length === 0
  ) {
    return {
      isValid: false,
      errorCode: 'missing-metrics',
    };
    /// make sure the date range is valid
  } else if (!isValidDateRange(settings?.timeframe)) {
    return {
      isValid: false,
      errorCode: 'missing-baseline-date',
    };
  } else {
    return {
      isValid: true,
    };
  }
};

/**
 * @description ensures that at least one block result has data
 * @param results
 */

const validateData = (results: IBlockResult[]): IValidationResult => {
  const isValid = results.length > 0 ? true : false;
  if (isValid) {
    return {
      isValid: true,
      errorCode: null,
    };
  } else {
    return {
      isValid: false,
      errorCode: 'insufficient-data',
    };
  }
};

export const blockTrendQuery = (
  args: IRangeQueryArgs
): Observable<IComputedAggregate[]> => {
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<IComputedAggregate[]> => {
      return rangeQuery({
        firestore: user.services.firestore,
        orgId: user.orgId,
        entityType: 'athletes',
        entityId: args.entityId,
        queryHash: args.queryHash,
        range: args.range,
      });
    }),
    switchMap(
      (values: IComputedAggregate[]): Observable<IComputedAggregate[]> =>
        mergeMetrics(values)
    )
  );
};
